import { Container, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { LoadingScreen } from '@components/LoadingScreen';
import * as Sentry from '@sentry/react';
import MenuLogo from 'src/layouts/dashboard/MenuLogo';
import InactiveUserPage from 'src/pages/InnactiveUser';
import PageErrorMFA from 'src/pages/PageErrorMFA';
import PageUnavailable from 'src/pages/PageUnavailable';
import UserNotExist from 'src/pages/UserNotExist';

import { rdBackSignalr } from '@src/api/signalr';
import { getCompanyFromHost } from '@utils/url';

import { RootState } from '@redux/store';

import { useAuth } from '../Auth/authContext';
import { useMe } from '@hooks/useMe';

interface UserProfileGuardProps {
  children: React.ReactElement;
}

const sxHeader = {
  height: '120px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 40px',
};

const sxLink = {
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
};

export const PageWrapper = ({ children }: UserProfileGuardProps) => {
  const { logout } = useAuth();

  return (
    <>
      <Stack sx={sxHeader}>
        <MenuLogo withoutStyles />
        <Typography variant="subtitle2" sx={sxLink} onClick={logout}>
          Log out
        </Typography>
      </Stack>
      <Container component="main">
        <Stack
          spacing={5}
          sx={{
            m: 'auto',
            maxWidth: 480,
            textAlign: 'center',
            justifyContent: 'center',
          }}
        >
          {children}
        </Stack>
      </Container>
    </>
  );
};

const getProfileErrorPage = (error: string) => {
  switch (error) {
    case 'ProfileNotActive':
      return <InactiveUserPage />;

    case 'CompanyRequiresMfa': {
      return <PageErrorMFA />;
    }

    case 'CompanyCreationDisabled':
    case 'ProfileCreationDisabled':
      return <PageUnavailable />;

    case 'UnacceptableRole':
    case 'ProfileNotCreated':
      return <UserNotExist />;

    default:
      return <PageUnavailable />;
  }
};

export const UserProfileGuard = ({ children }: UserProfileGuardProps) => {
  const { user, login, isLogout } = useAuth();
  const { currentUser: userProfile, isLoading, errorProfile } = useMe(!user)

  const isErrorMFA = useSelector((state: RootState) => state.auth.isErrorMFA);

  const accessToken = user?.access_token;

  useEffect(() => {
    if (!accessToken || !userProfile || rdBackSignalr.connectionState !== 0 || !user) return;
    rdBackSignalr.connect(accessToken);

    return () => {
      rdBackSignalr.disconnect();
    };
  }, [accessToken, user, userProfile]);

  useEffect(() => {
    if (!userProfile) return;
    Sentry.setUser({
      id: userProfile.id,
      username: `${userProfile.firstName} ${userProfile.lastName} (${userProfile.company?.name || '-'}: ${
        userProfile.userName
      })`,
      email: userProfile.email,
    });
  }, [userProfile]);

  if ((!user && !isLogout) || (errorProfile && 'status' in errorProfile && errorProfile?.status === 401)) {
    login();
    return <LoadingScreen />;
  }

  if (errorProfile && 'data' in errorProfile) {
    return <PageWrapper>{getProfileErrorPage(errorProfile?.data as string)}</PageWrapper>;
  }

  if (
    (!userProfile && isLoading) ||
    (userProfile?.company && getCompanyFromHost() !== userProfile?.company?.subdomain)
  ) {
    return <LoadingScreen />;
  }

  if (isErrorMFA) {
    return (
      <PageWrapper>
        <PageErrorMFA />
      </PageWrapper>
    );
  }

  return children;
};
